// Receipts and statements are rendered from the DOM rather than drawn into the
// PDF field by field. That keeps one source of truth for the layout, and it is
// the only approach that gets Arabic and Kurdish names right - the browser has
// already shaped and ordered the text by the time it is captured.

// Receipts are single slips - portrait suits them. Statements/reports are wide
// tables (date, description, debit/credit x2 currencies), so they go out in
// landscape. Both constants matter: passing orientation:'landscape' to jsPDF
// alone is not enough, since the usable-area math below is derived from these.
const A4 = { width: 595.28, height: 841.89 };
const A4_LANDSCAPE = { width: 841.89, height: 595.28 };
const MARGIN = 24;

async function renderToCanvas(element) {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas(element, {
    scale: Math.min(2, window.devicePixelRatio || 1) * 1.5,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: element.scrollWidth,
  });
}

/**
 * A single standard A4 portrait page, with the receipt scaled to fit inside
 * it (never stretched past the margins). A custom page size sized exactly to
 * the content's aspect ratio was tried here before and produced pages some
 * PDF readers do not handle well (very tall/short non-standard dimensions
 * silently clip content) - a fixed, standard page size sidesteps that
 * entirely and matches the same fit-to-page technique already used below for
 * statements.
 */
export async function downloadReceiptPdf(element, filename) {
  const { jsPDF } = await import('jspdf');
  const canvas = await renderToCanvas(element);

  const usableWidth = A4.width - MARGIN * 2;
  const usableHeight = A4.height - MARGIN * 2;
  const scale = Math.min(usableWidth / canvas.width, usableHeight / canvas.height);
  const drawWidth = canvas.width * scale;
  const drawHeight = canvas.height * scale;
  // Centered rather than pinned to the margin corner - receipts vary in
  // length, so whichever dimension isn't the binding constraint would
  // otherwise leave lopsided empty space on one side.
  const x = MARGIN + (usableWidth - drawWidth) / 2;
  const y = MARGIN + (usableHeight - drawHeight) / 2;

  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', x, y, drawWidth, drawHeight, undefined, 'FAST');
  pdf.save(filename);
}

/** A4 landscape pages with the content sliced across them - for account statements. */
export async function downloadDocumentPdf(element, filename) {
  const { jsPDF } = await import('jspdf');
  const canvas = await renderToCanvas(element);

  const usableWidth = A4_LANDSCAPE.width - MARGIN * 2;
  const usableHeight = A4_LANDSCAPE.height - MARGIN * 2;
  const scale = usableWidth / canvas.width;
  const sliceHeightPx = Math.floor(usableHeight / scale);

  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const slice = document.createElement('canvas');
  const ctx = slice.getContext('2d');

  let offset = 0;
  let page = 0;
  while (offset < canvas.height) {
    const height = Math.min(sliceHeightPx, canvas.height - offset);
    slice.width = canvas.width;
    slice.height = height;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, offset, canvas.width, height, 0, 0, canvas.width, height);

    if (page > 0) pdf.addPage();
    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.95),
      'JPEG',
      MARGIN,
      MARGIN,
      usableWidth,
      height * scale,
      undefined,
      'FAST',
    );
    offset += height;
    page += 1;
  }

  pdf.save(filename);
}

/**
 * Prints just the given element, using the print stylesheet in index.css.
 * `paged` picks the same landscape page as downloadDocumentPdf for a wide
 * tabular statement/report; a single-item receipt prints portrait.
 */
export function printElement(element, paged = false) {
  document.body.classList.add('printing');
  element.classList.add('print-target');
  if (paged) element.classList.add('print-landscape');
  const cleanUp = () => {
    document.body.classList.remove('printing');
    element.classList.remove('print-target', 'print-landscape');
    window.removeEventListener('afterprint', cleanUp);
  };
  window.addEventListener('afterprint', cleanUp);
  window.print();
  // Safari on iOS does not always fire afterprint.
  setTimeout(cleanUp, 2000);
}
